import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import DatePicker from 'react-datepicker';
import { ButtonSwitch } from 'lib/components';
import { combineDateAndTime } from 'lib/utils/datetime';
import { theme } from 'lib/style';
import { IoMdCalendar } from 'react-icons/io';
import { BsClockFill } from 'react-icons/bs';

const CustomTimeWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const InputDateWrapper = styled.div<{ width?: string }>`
  position: relative;
  height: 40px;
  input {
    padding: 6px 8px 8px 40px;
    width: ${({ width }) => width};
    border-radius: 6px;
    border: 1px solid rgba(0, 27, 83, 0.2);
    font-size: 16px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    color: ${theme.palette.gray100};
  }
  .hideCursor {
    caret-color: transparent;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  color: ${theme.palette.gray60};
  top: 8px;
  left: 8px;
  z-index: 1;
`;

const ErrorMessage = styled.div`
  margin-top: 10px;
  color: red;
`;

interface CustomDateTimePickerProps {
  setDate: (date: Date) => void;
  date: Date | null;
  useTimeSelectDropdown?: boolean;
  disabled?: boolean;
  hideDatePicker?: boolean;
  setErrorMessage?: (value: string) => void;
  errorMessage?: string;
  allowCustomTime?: boolean;
}

const MERIDIEM = {
  AM: 'am',
  PM: 'pm',
};
const meridiemValues = [
  {
    value: MERIDIEM.AM,
    text: 'AM',
  },
  {
    value: MERIDIEM.PM,
    text: 'PM',
  },
];

const isAmPm = (hours?: number) => {
  return hours && hours >= 12 ? MERIDIEM.PM : MERIDIEM.AM;
};

const CustomDateTimePicker = ({
  setDate,
  date,
  useTimeSelectDropdown = false,
  disabled = false,
  hideDatePicker = false,
  setErrorMessage,
  errorMessage,
  allowCustomTime = true,
}: CustomDateTimePickerProps) => {
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [meridiem, setMeridiem] = useState<string>(MERIDIEM.AM);

  const handleDateTimeChange = (
    startDate: Date,
    startTime: Date,
    meridiem: string
  ) => {
    const combinedDateTime = new Date(startDate);
    combinedDateTime.setHours(
      meridiem === MERIDIEM.PM
        ? startTime.getHours() + 12
        : startTime.getHours(),
      startTime.getMinutes()
    );

    const current = new Date();
    if (
      startDate.toDateString() === current.toDateString() &&
      combinedDateTime < current
    ) {
      if (setErrorMessage) {
        setErrorMessage(
          'Invalid selection. Please choose a time that is in the future.'
        );
      }
      return;
    }

    setDate(combineDateAndTime(startDate, startTime, meridiem));
  };

  const parseDate = (date: Date) => {
    return new Date(Date.parse(date as unknown as string));
  };

  useEffect(() => {
    if (date) {
      setMeridiem(isAmPm(parseDate(date).getHours()));
      setStartDate(parseDate(date));
      setStartTime(parseDate(date));
    }
  }, [date]);

  const handleDateChange = (
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    if (!event) return;

    const input = event.target as HTMLInputElement;
    const inputValue = input.value || startDate.toString();
    if (setErrorMessage) {
      setErrorMessage(
        isNaN(Date.parse(inputValue)) ? 'Please enter a valid date/time' : ''
      );
    }
  };

  const handleTimeChange = (
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    if (!event) return;

    const input = event.target as HTMLInputElement;
    const inputValue = input.value;
    if (setErrorMessage) {
      setErrorMessage(
        !/^(0?[1-9]|1[0-2]):[0-5][0-9]$/.test(inputValue)
          ? 'Please enter a valid date/time'
          : ''
      );
    }
  };

  const getMinTime = () => {
    const current = new Date();
    if (startDate.toDateString() === current.toDateString()) {
      const minTime = new Date();
      const nextInterval = Math.ceil(current.getMinutes() / 15) * 15;
      minTime.setHours(
        current.getHours(),
        nextInterval < 60 ? nextInterval : 0
      );
      if (nextInterval >= 60) {
        minTime.setHours(minTime.getHours() + 1);
      }
      return minTime;
    }

    return new Date(startDate.setHours(0, 0, 0, 0));
  };

  const getMaxTime = () => {
    return new Date(startDate.setHours(23, 45)); // 11:45 PM for future dates
  };

  const canSelectAM = () => {
    const amTime = new Date(startTime);
    amTime.setHours(amTime.getHours() % 12); // Adjust to AM hour
    return amTime.getTime() >= getMinTime().getTime();
  };

  const canSelectPM = () => {
    const pmTime = new Date(startTime);
    pmTime.setHours((pmTime.getHours() % 12) + 12); // Adjust to PM hour
    return pmTime.getTime() >= getMinTime().getTime();
  };

  const canSelectAMPM = () => {
    return canSelectAM() && canSelectPM();
  };
  return (
    <>
      <CustomTimeWrapper>
        {!hideDatePicker && (
          <InputDateWrapper width='97px'>
            <IconWrapper>
              <IoMdCalendar size={18} />
            </IconWrapper>

            <DatePicker
              onChange={(date: Date | null) => {
                if (!date) {
                  return;
                }
                handleDateTimeChange(date, startTime, meridiem);
              }}
              onChangeRaw={handleDateChange}
              selected={startDate}
              minDate={new Date()}
              disabled={disabled}
            />
          </InputDateWrapper>
        )}
        <InputDateWrapper width='40px'>
          <IconWrapper>
            <BsClockFill size={22} />
          </IconWrapper>
          <DatePicker
            selected={startTime}
            onChange={(time: Date | null) => {
              if (!time) {
                return;
              }
              handleDateTimeChange(startDate, time, meridiem);
            }}
            onChangeRaw={handleTimeChange}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeFormat='h:mm'
            timeCaption='Time'
            dateFormat='h:mm'
            minTime={getMinTime()}
            maxTime={getMaxTime()}
            open={useTimeSelectDropdown ? undefined : false}
            disabled={disabled}
            {...(!allowCustomTime && { onKeyDown: e => e.preventDefault() })}
            {...(!allowCustomTime && { className: 'hideCursor' })}
          />
        </InputDateWrapper>
        <InputDateWrapper width='23px'>
          <ButtonSwitch
            key={meridiem}
            defaultValue={meridiem}
            values={meridiemValues}
            onChange={newValue => {
              setMeridiem(newValue);
              handleDateTimeChange(startDate, startTime, newValue);
            }}
            disabled={disabled || !canSelectAMPM()}
          />
        </InputDateWrapper>
      </CustomTimeWrapper>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </>
  );
};

export default CustomDateTimePicker;
